'use client'

/**
 * Shared utilities for pulling structured information out of the script
 * editor. Anything that needs to read script content from the Lexical
 * editor state should go through this file so the conventions
 * (boundary detection, inline directives, serialization form) live in
 * one place.
 *
 * The text produced by these helpers is the canonical, *serialized*
 * script form: plain text with mentions kept as `{<JSON>}` tokens
 * (matching `MentionNode.getTextContent()` and
 * `serializeEditorState`). Conversion of inline directives such as
 * break / emotion into a TTS-ready prompt is performed on the server
 * (`@kagami/shared`'s `convertScriptToSpeechPrompt`), so the same
 * notation can be reused for non-voice consumers without re-parsing.
 */

// Mentions that are part of the spoken line. They do NOT act as block
// boundaries when collecting a character's dialogue — only structural
// mentions (character / segment / scene / description / etc.) do.
const INLINE_SPEECH_MENTION_IDS = new Set(['break', 'emotion'])

// We intentionally avoid `instanceof MentionNode` here. Vite HMR can
// reload `MentionNode.jsx` and leave existing nodes as instances of
// the old class, in which case `instanceof` returns false and we'd
// silently drop them. The static `getType()` tag is HMR-safe.
export function isMentionLike(node) {
    return !!node && typeof node.getType === 'function' && node.getType() === 'mention'
}

export function isInlineSpeechMention(node) {
    if (!isMentionLike(node)) return false
    if (typeof node.getMentionType !== 'function') return false
    if (node.getMentionType() === 'character') return false
    return INLINE_SPEECH_MENTION_IDS.has(node.getMentionId())
}

/**
 * Serialize a single paragraph into script-notation text:
 *   - text nodes contribute their plain text,
 *   - inline speech mentions (break / emotion) are kept as their
 *     `{<JSON>}` token (the server converts them to SSML/inline tags),
 *   - structural mentions are dropped.
 */
function paragraphScriptText(paragraph) {
    const children = typeof paragraph.getChildren === 'function' ? paragraph.getChildren() : []
    return children
        .map(child => {
            if (isInlineSpeechMention(child)) return child.getTextContent()
            if (isMentionLike(child)) return ''
            return child.getTextContent()
        })
        .join('')
}

/**
 * Collect only the speech that follows a specific character mention.
 * The block ends at:
 *   - an empty paragraph (blank line),
 *   - a paragraph containing any non-inline mention (character /
 *     segment / scene / description / etc.),
 *   - or the end of the document.
 *
 * The returned string is in serialized script notation; pass it to the
 * server, which is the single point that converts it into a TTS prompt.
 *
 * Must be called from inside an `editor.read()` / editor state read.
 */
export function $extractSpeechAfterMention(mentionNode) {
    const parent = mentionNode?.getParent?.()
    if (!parent) return ''

    const lines = []
    let current = parent.getNextSibling()

    while (current) {
        const children = typeof current.getChildren === 'function' ? current.getChildren() : []

        const hasBoundaryMention = children.some(c => isMentionLike(c) && !isInlineSpeechMention(c))
        if (hasBoundaryMention) break

        const text = paragraphScriptText(current)
        if (text.trim() === '') break

        lines.push(text)
        current = current.getNextSibling()
    }

    return lines.join('\n').trim()
}
